import React, { useCallback, useEffect, useState } from 'react'
import { FieldErrors, FormError, Setters, State } from '../../../../../../hooks/useForm'
import InlineGroup from '../../../../../atoms/inlinegroup/InlineGroup'
import Toggle from '../../../../../atoms/toggle/Toggle'
import VerticalGroup from '../../../../../atoms/verticalgroup/VerticalGroup'
import { ChoiceField, createCheckboxesChoice } from '../FormFields/ChoiceField'
import { getFieldError } from '../FormFields/getFieldError'
import { WrittenResponseField } from '../FormFields/WrittenResponseField'

export const DEFAULT_CHOICES: API.AssessmentQuestionFieldRequest[] = [
  createCheckboxesChoice({
    label: 'Yes',
    markCorrect: false
  }),
  createCheckboxesChoice({
    label: 'No',
    markCorrect: false
  }),
  createCheckboxesChoice()
]

interface Props {
  question: State['formData']
  setField: Setters['setField']
  setFieldSimple: Setters['setFieldSimple']
  setFieldRaw: Setters['setFieldRaw']
  fieldErrors?: FieldErrors
}

export const validateCheckboxForm = (question: State['formData'], errors: FormError[]) => {
  const fields = question.fields

  fields.forEach((field: API.AssessmentQuestionFieldRequest, index: number) => {
    switch (field.type) {
      case 'checkboxes':
        if (!field.config.label) {
          errors.push({ field: `field[${index}].label`, message: 'choice text can not be empty' })
        }
        break
      case 'written_response':
        if (field.config.hasWordLimit) {
          if (typeof field.config.wordLimit === 'undefined') {
            errors.push({ field: `field[${index}].wordLimit`, message: 'word limit is required' })
          }
          if (Number(field.config.wordLimit) <= 0 || !field.config.wordLimit) {
            errors.push({ field: `field[${index}].wordLimit`, message: 'word limit must greater than 0' })
          }
        }
        break
    }
  })
}

export const CheckboxesFormDetails: React.FC<Props> = ({ question, setFieldSimple, setFieldRaw, fieldErrors }) => {
  const [choices, updateChoices] = useState<API.CheckboxesFieldRequest[]>(
    question.fields.filter((field: API.AssessmentQuestionFieldRequest) => {
      return field.type === 'checkboxes'
    })
  )

  const [writtenResponeField, setWrittenResponseField] = useState<API.WrittenResponseFieldRequest | null>(() => {
    const field = question.fields.find((field: API.AssessmentQuestionFieldRequest) => {
      return field.type === 'written_response'
    })
    if (field) {
      return field
    }
    return null
  })

  const [hasOtherAnswer, setHasOtherAnswer] = useState<boolean>(writtenResponeField !== null)

  const onUpdateChoice = useCallback((newChoice: API.CheckboxesFieldRequest, index: number) => {
    updateChoices(choices => {
      return choices.map((choice, idx) => {
        if (idx !== index) {
          if (newChoice.config.markCorrect) {
            return {
              ...choice,
              config: {
                ...choice.config,
                markCorrect: false,
                mark: 0,
              }
            }
          }
          return choice
        }

        return {
          ...newChoice
        }
      })
    })
  }, [])

  const onAddNewEmptyChoiceBefore = useCallback((index: number) => {
    updateChoices(choices => {
      return choices.flatMap((choice, idx) => {
        if (idx !== index) return choice
        return [createCheckboxesChoice(), choice]
      })
    })
  }, [])

  const onRemoveChoice = useCallback((index: number) => {
    updateChoices(choices => {
      return choices.filter((_choice, idx) => {
        return idx !== index
      })
    })
  }, [])

  const onToggleHasAnotherAnswer = useCallback((value: boolean) => {
    setHasOtherAnswer(value)
    if (value) {
      setWrittenResponseField({
        type: 'written_response',
        config: {
          type: 'written_response',
          hasWordLimit: false,
          label: 'Other',
        }
      })
    } else {
      setWrittenResponseField(null)
    }
  }, [])

  useEffect(() => {
    const fields: API.AssessmentQuestionFieldRequest[] = [
      ...choices,
      ...(writtenResponeField ? [writtenResponeField] : [])
    ]

    setFieldRaw('marks', 0)
    setFieldRaw('fields', fields)
  }, [choices, setFieldRaw, writtenResponeField])

  const shouldFocusAtField = choices
    .reduce((result, field, index) => {
      const { max } = result
      if (typeof field.newlyCreatedAt === 'undefined')
        return result

      if (max) {
        if (field.newlyCreatedAt > max) {
          result.max = field.newlyCreatedAt
          result.index = index
        }
      } else {
        result.max = field.newlyCreatedAt
        result.index = index
      }
      return result
    }, { max: -1, index: -1 }) 

  return <VerticalGroup spaceBetweenElements={4}>
    <InlineGroup block>
      <Toggle
        reverse
        label="Assign points"
        checked={question.assignMarks}
        onUpdate={setFieldSimple('assignMarks')} />
    </InlineGroup>

    <p>Select the correct answer below</p>

    <VerticalGroup spaceBetweenElements={4}>
      {choices.map((choice, idx) => {
        const fieldError = getFieldError(idx, 'label', fieldErrors)
        return <ChoiceField
          key={`choice-${idx}-${choice.newlyCreatedAt}`}
          choice={choice}
          index={idx}
          onChange={onUpdateChoice}
          onNewChoiceBefore={onAddNewEmptyChoiceBefore}
          onRemoveChoice={onRemoveChoice}
          assignMarks={question.assignMarks}
          error={fieldError}
          autoFocus={shouldFocusAtField.index === idx}
        />
      })}
    </VerticalGroup>

    <hr />

    <Toggle
      reverse
      label='Add an "other" answer for comments'
      checked={hasOtherAnswer}
      onUpdate={onToggleHasAnotherAnswer}
    />

    {hasOtherAnswer && writtenResponeField && <WrittenResponseField
      onChange={setWrittenResponseField}
      writtenResponseField={writtenResponeField}
      index={choices.length}
      fieldErrors={fieldErrors}
    />}
  </VerticalGroup>
} 
